import { useMemo } from "react"
import type { Column } from "@tanstack/react-table"
import { useTableContext } from "../table.context"
import type {
  SummaryAggregationType,
  SummaryResult,
  SummaryValueKind,
} from "./table.summary.types"
import {
  collectSummaryValues,
  computeSummary,
  detectSummaryValueKind,
  type SummaryContribution,
} from "./table.summary.utils"

type SummaryValues = {
  values: SummaryContribution[]
  kind: SummaryValueKind
}

/**
 * A column's values in the current view — every loaded row that passes the
 * filters, across pages and groups — and the kind they add up to.
 *
 * Memoised on the filtered row model, whose identity TanStack only changes
 * when the data or a filter does. The table re-renders every context
 * consumer on each state change (a column resize is dozens a second), and
 * re-walking thousands of rows on each of those is a cost worth caching.
 */
export function useSummaryValues<TData>(
  column: Column<TData, unknown>,
): SummaryValues {
  const { table } = useTableContext<TData>()
  const { rows } = table.getFilteredRowModel()

  return useMemo(() => {
    const values = collectSummaryValues(rows, column)
    return { values, kind: detectSummaryValueKind(values) }
  }, [rows, column])
}

/** The result of the calculation a column's summary cell shows, if any. */
export function useColumnSummary<TData>(
  column: Column<TData, unknown>,
  type: SummaryAggregationType | undefined,
): SummaryResult | null {
  const { values, kind } = useSummaryValues(column)

  return useMemo(
    () => (type ? computeSummary({ values, type, kind }) : null),
    [values, kind, type],
  )
}
